import React from 'react';
import { SBA, SUBJECTS_THEME } from '../../theme';

/**
 * Live student graphs when `records` is provided; otherwise empty preview.
 */
export default function ProgressGraphs({ records = null }) {
  const live = Boolean(records);
  const weeks = ['W1', 'W2', 'W3', 'W4', 'W5', 'W6'];
  const weekly = records?.weeklyPercents?.length === 6 ? records.weeklyPercents : [0, 0, 0, 0, 0, 0];
  const subjects = records?.subjects || SUBJECTS_THEME.map((s) => ({ ...s, pct: 0, examPct: null }));
  const overallPct = Number(records?.overallPct) || 0;
  const maxY = 116;
  const minY = 20;
  const points = weekly
    .map((pct, i) => {
      const x = 30 + i * 57;
      const y = maxY - ((Math.min(100, pct) / 100) * (maxY - minY));
      return `${x},${y}`;
    })
    .join(' ');

  return (
    <div style={p.grid}>
      <div style={p.card}>
        <div style={p.cardHead}>
          <h3 style={p.cardTitle}>Student progress</h3>
          <span style={live ? p.liveBadge : p.demoBadge}>{live ? `${overallPct}% complete` : 'No records yet'}</span>
        </div>
        <div style={p.chartWrap}>
          <svg viewBox="0 0 320 140" style={{ width: '100%', height: 140 }} aria-label="Weekly study progress">
            {[0, 1, 2, 3].map((i) => (
              <line
                key={i}
                x1="30"
                x2="315"
                y1={20 + i * 32}
                y2={20 + i * 32}
                stroke="#e2e8f0"
                strokeWidth="1"
              />
            ))}
            {['100%', '66%', '33%', '0%'].map((t, i) => (
              <text key={t} x="2" y={24 + i * 32} fontSize="9" fill="#94a3b8">
                {t}
              </text>
            ))}
            <polyline
              points={points}
              fill="none"
              stroke={SBA.gold}
              strokeWidth="2.5"
              strokeLinejoin="round"
              strokeLinecap="round"
            />
            {weekly.map((pct, i) => (
              <circle key={weeks[i]} cx={30 + i * 57} cy={maxY - ((Math.min(100, pct) / 100) * (maxY - minY))} r="3.5" fill={SBA.navy} />
            ))}
            {weeks.map((w, i) => (
              <text key={w} x={26 + i * 57} y="134" fontSize="9" fill="#94a3b8">
                {w}
              </text>
            ))}
          </svg>
        </div>
        <div style={p.legend}>
          <span style={p.legendItem}>
            <span style={{ ...p.legendDot, background: SBA.gold }} /> Weekly study activity
          </span>
        </div>
      </div>

      <div style={p.card}>
        <div style={p.cardHead}>
          <h3 style={p.cardTitle}>Subject scores</h3>
          <span style={live ? p.liveBadge : p.demoBadge}>{live ? 'Live' : 'No records yet'}</span>
        </div>
        <div style={p.barsWrap}>
          {subjects.map((sub) => (
            <div key={sub.id || sub.label} style={p.barRow}>
              <span style={p.barLabel}>{sub.label}</span>
              <div style={p.barTrack}>
                <div style={{ ...p.barFill, width: `${sub.pct || 0}%`, background: sub.color }} />
              </div>
              <span style={{ ...p.barValue, color: sub.pct ? SBA.navy : '#94a3b8' }}>
                {sub.examPct != null ? `${sub.examPct}%` : sub.pct ? `${sub.pct}%` : '—'}
              </span>
            </div>
          ))}
        </div>
      </div>

      <div style={p.card}>
        <div style={p.cardHead}>
          <h3 style={p.cardTitle}>Quiz records</h3>
          <span style={live ? p.liveBadge : p.demoBadge}>
            {live && records?.quizAvgPct ? `${records.quizAvgPct}% avg` : live ? 'Live' : 'No records yet'}
          </span>
        </div>
        <div style={p.barsWrap}>
          {[
            { label: 'Lecture quizzes passed', pct: records?.quizLecturePct || 0, color: SBA.navy },
            { label: 'Module quizzes passed', pct: records?.quizModulePct || 0, color: SBA.gold },
            { label: 'Average quiz score', pct: records?.quizAvgPct || 0, color: '#059669' },
          ].map((row) => (
            <div key={row.label} style={p.barRow}>
              <span style={p.barLabel}>{row.label}</span>
              <div style={p.barTrack}>
                <div style={{ ...p.barFill, width: `${row.pct || 0}%`, background: row.color }} />
              </div>
              <span style={{ ...p.barValue, color: row.pct ? SBA.navy : '#94a3b8' }}>
                {live ? `${row.pct || 0}%` : '—'}
              </span>
            </div>
          ))}
        </div>
      </div>

      <div style={p.card}>
        <div style={p.cardHead}>
          <h3 style={p.cardTitle}>Reports</h3>
          <span style={live ? p.liveBadge : p.demoBadge}>{live ? 'From your record' : 'No records yet'}</span>
        </div>
        <div style={p.reportList}>
          {(records?.reports || [
            { icon: 'quiz', label: 'Practice exam report', hint: 'Generated after each AI practice exam', status: 'Nil' },
            { icon: 'insights', label: 'Weekly study report', hint: 'Time studied, lectures done, quiz scores', status: 'Nil' },
            { icon: 'workspace_premium', label: 'Exam readiness report', hint: 'AI prediction of your GED readiness', status: 'Nil' },
          ]).map(({ icon, label, hint, status }) => (
            <div key={label} style={p.reportRow}>
              <div style={p.reportIcon}>
                <span className="material-symbols-outlined" style={{ fontSize: 20, color: SBA.navy }}>
                  {icon}
                </span>
              </div>
              <div style={{ flex: 1 }}>
                <p style={p.reportLabel}>{label}</p>
                <p style={p.reportHint}>{hint}</p>
              </div>
              <span style={status && status !== 'Nil' ? p.reportLive : p.reportNil}>{status || 'Nil'}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const p = {
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: 16,
    marginBottom: 28,
  },
  card: {
    background: '#fff',
    borderRadius: 20,
    padding: 18,
    border: '1px solid #e2e8f0',
    boxShadow: '0 4px 20px rgba(27,54,93,0.05)',
  },
  cardHead: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 10,
    marginBottom: 12,
  },
  cardTitle: { margin: 0, fontSize: 15, fontWeight: 800, color: SBA.navy },
  demoBadge: {
    fontSize: 10,
    fontWeight: 800,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    color: SBA.goldStrong,
    background: 'rgba(197,160,70,0.12)',
    border: '1px solid rgba(197,160,70,0.35)',
    borderRadius: 999,
    padding: '3px 8px',
    whiteSpace: 'nowrap',
  },
  liveBadge: {
    fontSize: 10,
    fontWeight: 800,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    color: '#047857',
    background: '#ecfdf5',
    border: '1px solid #a7f3d0',
    borderRadius: 999,
    padding: '3px 8px',
    whiteSpace: 'nowrap',
  },
  chartWrap: { marginBottom: 8 },
  legend: { display: 'flex', flexWrap: 'wrap', gap: 12 },
  legendItem: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: SBA.onSurfaceVariant },
  legendDot: { width: 10, height: 10, borderRadius: 3, display: 'inline-block' },
  barsWrap: { display: 'grid', gap: 14, paddingTop: 6 },
  barRow: { display: 'grid', gridTemplateColumns: '110px 1fr 40px', alignItems: 'center', gap: 10 },
  barLabel: { fontSize: 12, fontWeight: 600, color: SBA.onSurfaceVariant },
  barTrack: { height: 10, borderRadius: 999, background: '#eef2f7', overflow: 'hidden' },
  barFill: { height: '100%', borderRadius: 999, transition: 'width 0.4s' },
  barValue: { fontSize: 12, fontWeight: 700, textAlign: 'right' },
  reportList: { display: 'grid', gap: 10 },
  reportRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '10px 12px',
    borderRadius: 12,
    background: '#f8fafc',
    border: '1px solid #eef2f7',
  },
  reportIcon: {
    width: 36,
    height: 36,
    borderRadius: 10,
    background: '#eef2f7',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  reportLabel: { margin: 0, fontSize: 13, fontWeight: 700, color: SBA.navy },
  reportHint: { margin: 0, fontSize: 11, color: SBA.outline },
  reportNil: { fontSize: 11, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase' },
  reportLive: { fontSize: 11, fontWeight: 800, color: '#047857' },
};
